import React from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Dropdown, Layout, Menu, Space, Typography } from 'antd';
import {
  AppstoreOutlined,
  ApartmentOutlined,
  ContactsOutlined,
  BankOutlined,
  LogoutOutlined,
  UserOutlined,
  SolutionOutlined,
  ClusterOutlined,
  CalculatorOutlined,
  ScanOutlined,
  ScheduleOutlined,
  DollarOutlined,
  LineChartOutlined,
} from '@ant-design/icons';

import { useAuth } from '../authentication/AuthProvider';
import { BooksProvider, useCompanyId } from '../lib/books';
import ErrorBoundary from '../components/ErrorBoundary';
import { APP_VERSION } from '../lib/version';

const { Header, Content, Footer, Sider } = Layout;

/** Browser-tab titles per top-level route. */
const PAGE_TITLES = {
  '': 'Workspace', companies: 'Companies', departments: 'Departments', branches: 'Branches', designations: 'Career Levels', services: 'Services',
  users: 'Staff / Users', timesheets: 'Timesheets', leads: 'Leads', proposals: 'Proposals', associates: 'Associates', clients: 'Clients',
  'engagement-letters': 'Engagement Letters', 'scan-n-read': 'ScanNRead', workbook: 'Workbook', engagements: 'Service Engagements', projects: 'Projects',
  tasks: 'Tasks', deadlines: 'Deadlines', books: 'Client Books', counterparties: 'Customers / Suppliers', journal: 'Transaction Posting',
  'ledger-reports': 'Ledger Reports', 'fixed-assets': 'Fixed Assets', budgets: 'Budgeting', 'chart-of-accounts': 'Chart of Accounts', 'vat-setup': 'VAT Setup',
  'vat-reports': 'VAT Reports', invoices: 'Invoices', payments: 'Payments', 'credit-notes': 'Credit Notes', reports: 'Performance Analysis',
  portal: 'Client Portal', account: 'My account',
};

/** The company shown in the header: the user's own, or the one a System Administrator is working on. */
function HeaderCompany({ fallback }) {
  const { companyId, companies, isSystemAdmin } = useCompanyId();
  if (isSystemAdmin) {
    const current = companies.find((c) => c.id === companyId);
    return <span className="app-header-company">{current ? current.name : 'System administration'}<Typography.Text type="secondary" style={{ marginLeft: 8, fontWeight: 'normal' }}>System Administrator</Typography.Text></span>;
  }
  return <span className="app-header-company">{fallback}</span>;
}

/**
 * ERPNext-Desk-style shell: dark left Sider grouped by module (Organization/
 * CRM/Clients/Setup, mirroring the ADMIN vs ERPSuite split from the earlier
 * ERPNext build), light content area, top-right user menu.
 */
export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const isSystemAdmin = auth.user?.role === 'SYSTEM_ADMIN';
  const isClientUser = auth.user?.role === 'CLIENT_ADMIN';

  // A client portal login gets its own, smaller menu: nothing about the firm's internals.
  const portalItems = [
    { key: 'portal', icon: <AppstoreOutlined />, label: <NavLink to="/portal">Home</NavLink> },
    { key: 'engagement-letters', icon: <SolutionOutlined />, label: <NavLink to="/engagement-letters">Engagement Letters</NavLink> },
    { key: 'invoices', icon: <DollarOutlined />, label: <NavLink to="/invoices">Invoices</NavLink> },
    { key: 'statement', icon: <CalculatorOutlined />, label: <NavLink to="/portal/statement">Statement of Account</NavLink> },
  ];

  const staffItems = [
    { key: 'workspace', icon: <AppstoreOutlined />, label: <NavLink to="/">Workspace</NavLink> },
    ...(isSystemAdmin
      ? [{ key: 'companies', icon: <BankOutlined />, label: <NavLink to="/companies">Companies</NavLink> }]
      : []),
    {
      key: 'organization',
      icon: <ApartmentOutlined />,
      label: 'Organization',
      children: [
        { key: 'departments', label: <NavLink to="/departments">Departments</NavLink> },
        { key: 'branches', label: <NavLink to="/branches">Branches</NavLink> },
        { key: 'designations', label: <NavLink to="/designations">Career Levels</NavLink> },
        { key: 'services', label: <NavLink to="/services">Services</NavLink> },
        { key: 'users', label: <NavLink to="/users">Staff / Users</NavLink> },
        { key: 'timesheets', label: <NavLink to="/timesheets">Timesheets</NavLink> },
      ],
    },
    {
      key: 'crm',
      icon: <ContactsOutlined />,
      label: 'CRM',
      children: [
        { key: 'leads', label: <NavLink to="/leads">Leads</NavLink> },
        { key: 'proposals', label: <NavLink to="/proposals">Proposals</NavLink> },
        { key: 'associates', label: <NavLink to="/associates">Associates</NavLink> },
      ],
    },
    {
      key: 'clients-group',
      icon: <SolutionOutlined />,
      label: 'Clients',
      children: [
        { key: 'clients', label: <NavLink to="/clients">Clients</NavLink> },
        { key: 'engagement-letters', label: <NavLink to="/engagement-letters">Engagement Letters</NavLink> },
      ],
    },
    {
      key: 'scan-n-read',
      icon: <ScanOutlined />,
      label: <NavLink to="/scan-n-read">ScanNRead</NavLink>,
    },
    {
      key: 'workflow',
      icon: <ScheduleOutlined />,
      label: 'Workflow',
      children: [
        { key: 'workbook', label: <NavLink to="/workbook">Workbook (board)</NavLink> },
        { key: 'engagements', label: <NavLink to="/engagements">Service Engagements</NavLink> },
        { key: 'projects', label: <NavLink to="/projects">Projects</NavLink> },
        { key: 'tasks', label: <NavLink to="/tasks">Tasks</NavLink> },
        { key: 'deadlines', label: <NavLink to="/deadlines">Deadlines</NavLink> },
      ],
    },
    {
      key: 'accounting',
      icon: <CalculatorOutlined />,
      label: 'Accounting',
      children: [
        { key: 'books', label: <NavLink to="/books">Client Books</NavLink> },
        { key: 'counterparties', label: <NavLink to="/counterparties">Customers / Suppliers</NavLink> },
        { key: 'journal', label: <NavLink to="/journal">Transaction Posting</NavLink> },
        { key: 'ledger-reports', label: <NavLink to="/ledger-reports">Ledger Reports</NavLink> },
        { key: 'fixed-assets', label: <NavLink to="/fixed-assets">Fixed Assets</NavLink> },
        { key: 'budgets', label: <NavLink to="/budgets">Budgeting</NavLink> },
        { key: 'chart-of-accounts', label: <NavLink to="/chart-of-accounts">Chart of Accounts</NavLink> },
        { key: 'vat-setup', label: <NavLink to="/vat-setup">VAT Setup</NavLink> },
        { key: 'vat-reports', label: <NavLink to="/vat-reports">VAT / VIES / OSS / IOSS Reports</NavLink> },
      ],
    },
    {
      key: 'billing',
      icon: <DollarOutlined />,
      label: 'Billing',
      children: [
        { key: 'invoices', label: <NavLink to="/invoices">Invoices</NavLink> },
        { key: 'payments', label: <NavLink to="/payments">Payments / Receipts</NavLink> },
        { key: 'credit-notes', label: <NavLink to="/credit-notes">Credit Notes</NavLink> },
      ],
    },
    {
      key: 'reports',
      icon: <LineChartOutlined />,
      label: <NavLink to="/reports">Performance Analysis</NavLink>,
    },
  ];

  const items = isClientUser ? portalItems : staffItems;

  const [selectedKeys, setSelectedKeys] = React.useState([]);
  React.useEffect(() => {
    const path = location.pathname.split('/')[1] || 'workspace';
    if (location.pathname === '/portal/statement') setSelectedKeys(['statement']);
    else setSelectedKeys([path === '' ? 'workspace' : path]);
    const title = PAGE_TITLES[location.pathname.split('/')[1] || ''];
    document.title = title ? `${title} - InstantRead PMS` : 'InstantRead PMS';
  }, [location]);

  const handleLogout = () => {
    auth.signOut();
    navigate('/auth/login', { replace: true });
  };

  // A temporary password has to be replaced before anything else is used.
  if (auth.user?.mustChangePassword && location.pathname !== '/account') {
    return <Navigate to="/account?required=1" replace />;
  }

  const shell = (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider breakpoint="lg" collapsedWidth="0" width={220} className="app-sider">
        <div className="app-brand">
          <ClusterOutlined style={{ marginRight: 10, fontSize: 18 }} /> InstantRead PMS
        </div>
        <Menu theme="dark" mode="inline" selectedKeys={selectedKeys} items={items} />
      </Sider>
      <Layout>
        <Header className="app-header">
          <HeaderCompany fallback={auth.user?.companyName || (isClientUser ? 'Client portal' : 'InstantRead PMS')} />
          <Dropdown
            menu={{
              items: [
                { key: 'account', icon: <UserOutlined />, label: 'My account', onClick: () => navigate('/account') },
                { type: 'divider' },
                { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: handleLogout },
              ],
            }}
            placement="bottomRight"
          >
            <Button type="text">
              <Space>
                <Avatar size={28} icon={<UserOutlined />} />
                <Typography.Text>{auth.user?.name}</Typography.Text>
              </Space>
            </Button>
          </Dropdown>
        </Header>
        <Content className="app-content">
          <div className="page-panel">
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </Content>
        <Footer className="app-footer">
          InstantRead PMS v{APP_VERSION} &copy; {new Date().getFullYear()} Prolab Consultants
        </Footer>
      </Layout>
    </Layout>
  );

  return isClientUser ? shell : <BooksProvider>{shell}</BooksProvider>;
}
